import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { InfoOutlined as InfoIcon } from "@mui/icons-material";
import { LineItemPreview } from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatDate } from "@/utils/formatters/dateFormatter";

/**
 * Dense read-only table of per-line claim items belonging to a parent
 * reimbursement.
 *
 * Sourced from the EOR per-line records the loader filters out before insert.
 * Surfaced here so reviewers can audit line counts + adjustment/denial sums
 * during review. After approve these are gone — the source PDF is the long-
 * term record. AB#4455.
 *
 * Uses a hand-tuned dense MUI table (rather than CompactTable) because line
 * items are pure read-only metadata — no row selection, no expand, no link.
 * Tighter padding + nowrap cells keep each row near-text-height even when a
 * parent reimbursement carries 10+ lines.
 */
interface ReimbursementLineItemsTableProps {
  lineItems: LineItemPreview[] | undefined;
  /** Optional caption — e.g. "Line items (7)". */
  title?: string;
}

// Format service dates through the same formatDate helper the parent
// reimbursement grid uses, so the expand panel reads in the project's
// canonical date convention (MM/DD/YYYY) rather than ISO strings.
// Equality compare runs on the formatted output so the single-day case
// (start == end) still collapses to one cell after formatting.
const formatServiceDates = (li: LineItemPreview): string => {
  if (!li.service_start_date && !li.service_end_date) return "—";
  const start = li.service_start_date ? formatDate(li.service_start_date) : "?";
  const end = li.service_end_date ? formatDate(li.service_end_date) : "?";
  if (start === end) return start;
  return `${start} → ${end}`;
};

// pickLineAmount returns the most useful per-line dollar value the template
// emitted, falling back across the schema fields. Templates can only map
// one source field per target — the per-line "Reimbursable" column may end
// up under reimbursement_amount, requested_amount, or billed_amount
// depending on whether the carrier's header-level total already claimed
// reimbursement_amount. Exported for unit testing.
export const pickLineAmount = (li: LineItemPreview): number | undefined => {
  if (typeof li.reimbursement_amount === "number" && li.reimbursement_amount !== 0) return li.reimbursement_amount;
  if (typeof li.requested_amount === "number" && li.requested_amount !== 0) return li.requested_amount;
  if (typeof li.billed_amount === "number" && li.billed_amount !== 0) return li.billed_amount;
  return undefined;
};

// Tight cell sx — used on every cell so the table reads as a dense reference
// grid rather than a row-spaced data table.
const cellSx = {
  py: 0.5,
  px: 1,
  fontSize: "0.75rem",
  whiteSpace: "nowrap" as const,
  overflow: "hidden" as const,
  textOverflow: "ellipsis" as const,
  borderColor: "divider",
};
const headerSx = {
  ...cellSx,
  fontWeight: 600,
  color: "text.secondary",
  textTransform: "uppercase" as const,
  letterSpacing: "0.04em",
  fontSize: "0.65rem",
};

export const ReimbursementLineItemsTable = ({ lineItems, title }: ReimbursementLineItemsTableProps) => {
  if (!lineItems || lineItems.length === 0) return null;

  return (
    <Stack spacing={0.75}>
      {/* Caption + display-only badge. The badge makes it explicit that line
          items are review-time context only — they aren't persisted on
          approve, so reviewers shouldn't expect to find them post-load. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
          {title ?? `Line items (${lineItems.length})`}
        </Typography>
        <Tooltip
          title="Line items are shown for review reference only. They are not saved on approve — the carrier total reconciles to a single reimbursement."
          placement="top"
          arrow
        >
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, color: "text.disabled" }}>
            <InfoIcon sx={{ fontSize: 12 }} />
            <Typography
              variant="caption"
              sx={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.04em" }}
            >
              Display only
            </Typography>
          </Box>
        </Tooltip>
      </Box>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerSx, width: "32%" }}>Provider</TableCell>
              <TableCell sx={{ ...headerSx, width: "22%" }}>Service dates</TableCell>
              <TableCell sx={{ ...headerSx, width: "16%" }}>Claim no</TableCell>
              <TableCell sx={{ ...headerSx, width: "15%", textAlign: "right" }}>Reimbursable</TableCell>
              <TableCell sx={{ ...headerSx, width: "15%", textAlign: "right" }}>Adjustment</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lineItems.map((li, idx) => (
              <TableRow key={`line_${li.extracted_record_index}_${idx}`} hover>
                <TableCell sx={cellSx}>
                  <Tooltip title={li.provider ?? ""} placement="top">
                    <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
                      {li.provider ?? "—"}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell sx={cellSx}>{formatServiceDates(li)}</TableCell>
                <TableCell sx={cellSx}>{li.reference_number ?? "—"}</TableCell>
                <TableCell sx={{ ...cellSx, textAlign: "right" }}>
                  {(() => {
                    const amount = pickLineAmount(li);
                    return typeof amount === "number" ? formatUSD(amount) : "—";
                  })()}
                </TableCell>
                <TableCell sx={{ ...cellSx, textAlign: "right" }}>
                  {typeof li.adjustment_amount === "number" && li.adjustment_amount !== 0
                    ? formatUSD(li.adjustment_amount)
                    : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Stack>
  );
};
